import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiPlus, FiX } from "react-icons/fi";
import SelectInput from "../formComp/SelectInputNew";
import TextInput from "../formComp/TextInputNew";
import BlueCard from "../formComp/BlueCardNew";
import {
  CrownBridgeForm,
  CrownBridgeProps,
  CrownBridgeOutput,
} from "../../../types/BlueCardTypes";

const CrownBridge: React.FC<CrownBridgeProps> = ({ onChange, filterToken }) => {
  const [cards, setCards] = useState<CrownBridgeForm[]>([
    {
      toothNumbers: [],
      material: "",
      prosthesisType: "",
      designType: "",
      shade: "",
    },
  ]);

  const [occlusalContact, setOcclusalContact] = useState("");
  const [proximalContact, setProximalContact] = useState("");
  const [ponticDesign, setPonticDesign] = useState("");

  const handleCardChange = (
    index: number,
    field: string,
    value: string | string[]
  ) => {
    setCards((prev) =>
      prev.map((card, i) => (i === index ? { ...card, [field]: value } : card))
    );
  };

  const handleAddCard = () => {
    setCards((prev) => [
      ...prev,
      {
        toothNumbers: [],
        material: "",
        prosthesisType: "",
        designType: "",
        shade: "",
      },
    ]);
  };

  const handleRemoveCard = (index: number) => {
    setCards((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const output: CrownBridgeOutput = {
      occlusalContact,
      proximalContact,
      ponticDesign,
      instanceDetails: cards,
    };
    onChange(output);
  }, [cards, occlusalContact, proximalContact, ponticDesign, onChange]);

  return (
    <div className="w-full text-[var(--cu-text)]">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-[var(--cu-border)] pb-2">
        <h2 className="text-sm font-medium text-[var(--cu-accent)]">
          Crown & Bridge
        </h2>
        <span className="rounded-full border border-[var(--cu-accent)]/30 bg-[var(--cu-accent-soft)] px-2 py-0.5 text-[9px] font-semibold text-[var(--cu-accent)]">
          {cards.length} unit{cards.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="space-y-2.5">
        <AnimatePresence initial={false}>
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="relative"
            >
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => handleRemoveCard(index)}
                  aria-label="Remove treatment unit"
                  className="absolute -right-1 -top-1 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--cu-border)] bg-[var(--cu-input)] text-[var(--cu-subtle)] shadow-sm transition hover:border-[var(--cu-danger)]/50 hover:text-[var(--cu-danger)]"
                >
                  <FiX className="h-3 w-3" />
                </button>
              )}
              {cards.length > 1 && (
                <div className="mb-1 flex items-center gap-1.5 px-0.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded bg-[var(--cu-accent)] text-[9px] font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--cu-subtle)]">
                    Unit
                  </span>
                </div>
              )}
              <BlueCard
                mode="crownBridge"
                cardData={{ card: card }}
                onChange={(field, value) =>
                  handleCardChange(index, field, value)
                }
                filterToken={filterToken}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <motion.button
        type="button"
        onClick={handleAddCard}
        whileTap={{ scale: 0.98 }}
        className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[var(--cu-accent)]/40 bg-[var(--cu-accent-soft)] px-3 py-2 text-xs font-semibold text-[var(--cu-accent)] transition hover:border-[#2fa9bf] hover:bg-[var(--cu-accent-soft)] md:w-auto"
      >
        <FiPlus className="h-3.5 w-3.5" />
        Add another Treatment / Material
      </motion.button>

      <details className="mt-3 rounded-xl border border-[var(--cu-border)] bg-[var(--cu-input)]">
        <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-[var(--cu-muted)] marker:content-none [&::-webkit-details-marker]:hidden">
          Contact & pontic preferences
          <span className="ml-2 font-normal normal-case tracking-normal text-[var(--cu-subtle)]">
            — optional, expand
          </span>
        </summary>
        <div className="grid grid-cols-1 gap-3 border-t border-[var(--cu-border)] px-3 pb-2 pt-3 md:grid-cols-2">
          <TextInput
            stacked
            label="Occlusal contact"
            name="occlusalContact"
            value={occlusalContact}
            onChange={(e) => setOcclusalContact(e.target.value)}
            optional
          />

          <TextInput
            stacked
            label="Proximal contact"
            name="proximalContact"
            value={proximalContact}
            onChange={(e) => setProximalContact(e.target.value)}
            optional
          />

          <div className="md:col-span-2">
            <SelectInput
              stacked
              label="Pontic design (if any)"
              name="ponticDesign"
              value={ponticDesign}
              onChange={(e) => setPonticDesign(e.target.value)}
              options={[
                { value: "modified", label: "Modified Ridge Lap" },
                { value: "ridge-lap", label: "Saddle Ridge Lap" },
                { value: "hygienic", label: "Hygienic" },
                { value: "conical", label: "Conical" },
                { value: "ovate", label: "Ovate" },
              ]}
              optional
            />
          </div>
        </div>
      </details>
    </div>
  );
};

export default CrownBridge;
